/**
 * Floating panel for layout debug recording controls.
 */

import React, { useEffect, useState } from 'react';
import {
  clearLayoutDebugTimeline,
  downloadLayoutDebugTimeline,
  getLayoutDebugTimeline,
} from '../utils/layoutDebug';

interface LayoutDebugPanelProps {
  enabled: boolean;
}

export const LayoutDebugPanel: React.FC<LayoutDebugPanelProps> = ({ enabled }) => {
  const [sampleCount, setSampleCount] = useState(0);
  const [eventCount, setEventCount] = useState(0);
  const [serverEventCount, setServerEventCount] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    const intervalId = window.setInterval(() => {
      const timeline = getLayoutDebugTimeline();
      setSampleCount(timeline.sampleCount);
      setEventCount(timeline.eventCount);
      setServerEventCount(timeline.serverEventCount);
    }, 500);

    return () => window.clearInterval(intervalId);
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div
      className="fixed bottom-20 left-4 z-[9999] rounded-md border border-amber-500/40 bg-background/95 p-3 text-xs shadow-lg backdrop-blur-sm"
      role="region"
      aria-label="Layout debug recorder"
    >
      <div className="mb-2 font-semibold text-amber-600">Layout debug recording</div>
      <div className="mb-2 text-muted-foreground">
        {sampleCount} samples · {eventCount} layout events · {serverEventCount} server events
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="rounded bg-amber-500 px-2 py-1 font-medium text-amber-950 hover:bg-amber-400"
          onClick={() => downloadLayoutDebugTimeline()}
        >
          Download JSON
        </button>
        <button
          type="button"
          className="rounded border border-border px-2 py-1 hover:bg-muted"
          onClick={() => {
            clearLayoutDebugTimeline();
            setSampleCount(0);
            setEventCount(0);
          }}
        >
          Clear
        </button>
      </div>
      <div className="mt-2 text-[10px] text-muted-foreground">
        Ctrl+Shift+L to download · console: window.__layoutDebug
      </div>
    </div>
  );
};
